import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import NewsFields from 'components/news/NewsFields';
import { ContentPreviewAction, previewImageUrl } from 'components/shared/content/ContentPreviewDialog';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultNewsFormValidations from 'common/validations/form/defaultNews';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { updateNewsInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { getFieldsToUpdate } from 'common/form/utils';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import newsSlice from 'store/modules/news';

const requiredFields = ['created_at', 'body', 'title'];

function NewsEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes']);
  const { id } = useParams();
  const pageTitle = t('news.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const news = useSelector((state) => state.news.data.currentNews);
  const status = useSelector((state) => state.news.status.general);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten `status.general`; sin este guard el find inicial rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(newsSlice.actions.find(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (news && Object.keys(news).length > 0 && news.id === id) {
      const newsData = updateNewsInitialValues(news);
      setInitialValues(newsData);
    }
  }, [news]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista SOLO después de una edición (submit) exitosa.
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listNews.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'news');
      hasSubmitted.current = true;
      dispatch(newsSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultNewsFormValidations}
        render={({ handleSubmit, invalid, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <NewsFields requiredFields={requiredFields} />
            <StyledButtonsContainer>
              <ContentPreviewAction
                title={values.title}
                date={values.created_at}
                imageUrl={previewImageUrl(values.featured_image)}
                body={values.body}
              />
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listNews.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('news.edit', { ns: 'buttons' })}
                type="submit"
              />
            </StyledButtonsContainer>
          </form>
        )}
      />
    </>
  );
}

export default NewsEdit;
